import { EventBus } from '@/helpers'

/**
 * Add payment type ad desclaimer for OMS salsforce
 * @return string
 * */
const paymentType = {
  'POS': 'Adyen_POS',
  'PAYBYMAIL': 'Adyen_PayByLink',
  'GIFT': 'GiftCertificate'
}

/**
 * Manage all payment method flow
 * @flow
 * ---
 * Map payments using PaymentToManage
 * @if PaymentToManage[paymentMethod] return function, call() it
 * @else use genericPayment
 * ---
 * @arguments
 * orderId
 * paymentMethod
 * @return Promise
 * */
const PaymentToManage = {
  'POS': posPayment,
  'adyen_pay_by_mail': payByMailPayment,
  'GIFT_CERTIFICATE': giftPayment
}

export function processPayment ({ paymentMethod = '' }) {
  let ctx = global.$store
  if (paymentMethod && paymentMethod.code) {
    let code = paymentMethod.code
    if (PaymentToManage[code]) {
      return PaymentToManage[code].call(this, ctx, code)
    } else {
      return genericPayment(ctx, code)
    }
  } else {
    return Promise.reject(new Error('No payment provided'))
  }
}

/**
 * Process order using Adyen POS Payment
 * @flow
 * Open modal to show loader and live messages: ctx.commit('pos/setPaymentMessage', ...)
 * Save payment method (now is static: AdyenComponent)
 * On success check for pos enabled or exist: pos/adyenGetPos
 * On success place order: checkout/placeOrder
 * @arguments
 * ctx :  Store
 * code:  Payment_method
 * @return order
 * */
function posPayment (ctx, code) {
  loadModal(ctx)
  let temporaryCode = 'AdyenComponent'
  setLiveMessage(ctx, 'checkout.live-message.check-for-terminal')
  return makeDispatch(ctx, { key: 'adyenGetPos' })
    .then(() => {
      setLiveMessage(ctx, 'checkout.live-message.set-payment-method')
      return ctx.dispatch('checkout/setPaymentMethod', {
        code: temporaryCode,
        type: paymentType['POS']
      })
        .then(() => {
          setLiveMessage(ctx, 'checkout.live-message.place-close-order')
          return ctx.dispatch('checkout/placeOrder')
            .then((order) => {
              // let placedOrder = order
              let orderId = order.order_id
              setLiveMessage(ctx, 'checkout.live-message.order-placed')
              return makeDispatch(ctx, { key: 'payByAdyenPOS', payloads: { orderId } })
                .then((resp) => {
                  let { pspReference, paymentBrand } = resp
                  setLiveMessage(ctx, 'checkout.live-message.payment-done')
                  let amount = ctx.getters['cart/totalAmount']
                  // update order with PSP REFERENCE
                  let payload = {
                    params: {
                      'c_Adyen_pspReference': pspReference,
                      'c_Adyen_eventCode': 'CAPTURE',
                      'c_Adyen_paymentMethod': paymentBrand,
                      'c_paymentMethod': 'AdyenComponent',
                      'c_Adyen_value': amount * 100,
                      'c_adyenAccount': process.env.VUE_APP_ADYEN_POS_MERCHANT,
                      'c_availableToExportHost': true,
                      'c_countryLocale': ctx.getters['storeConfig/locale'],
                      'c_isAllMirakl': false
                    }
                  }
                  return updateOrder(ctx, orderId, payload).then(() => { return orderId })
                })
                .catch((err) => {
                  // set order to status failed
                  updateOrder(ctx, orderId, { params: {}, status: 'failed' }).then(() => { return orderId })
                  global.$router.push({ name: 'home' })
                  ctx.dispatch('cart/empty')
                  ctx.dispatch('checkout/empty')
                  // eslint-disable-next-line
                  throw { err, orderId }
                })
            })
        })
    })
    .finally(() => {
      setLoader(ctx, false)
      ctx.commit('ui/hideLoader')
      EventBus.$emit('genericModal:closeModal')
    })
}

/**
 * process order using Adyen PayByMail process
 * @flow
 * Open modal to show loader and live messages: ctx.commit('pos/setPaymentMessage', ...)
 * Save payment method (now is static: AdyenComponent)
 * on success close order using payByAdyenMail
 * @arguments
 * ctx :  Store
 * code: Payment_method
 * @return order
 * */
function payByMailPayment (ctx, code) {
  loadModal(ctx)
  let temporaryCode = 'AdyenComponent'
  setLiveMessage(ctx, 'checkout.live-message.set-payment-method')
  return ctx.dispatch('checkout/setPaymentMethod', {
    code: temporaryCode,
    type: paymentType['PAYBYMAIL']
  })
    .then(() => {
      setLiveMessage(ctx, 'checkout.live-message.place-close-order')
      return ctx.dispatch('checkout/placeOrder')
        .then((order) => {
          const orderId = order.order_id
          setLiveMessage(ctx, 'checkout.live-message.order-placed')
          return makeDispatch(ctx, { key: 'payByAdyenMail', payloads: { orderId } })
            .then(() => {
              let payload = {
                params: {
                  'c_Adyen_value': 0,
                  'c_type': paymentType['PAYBYMAIL'],
                  'c_adyenAccount': process.env.VUE_APP_ADYEN_MERCHANT,
                  'c_paymentMethod': 'AdyenComponent',
                  'c_availableToExportHost': true,
                  'c_countryLocale': ctx.getters['storeConfig/locale'],
                  'c_isAllMirakl': false
                }
              }
              return updateOrder(ctx, orderId, payload).then(() => { return orderId })
            })
        })
    })
    .finally(() => {
      setLoader(ctx, false)
      ctx.commit('ui/hideLoader')
      EventBus.$emit('genericModal:closeModal')
    })
}

/**
 * Process order using Gift payment
 * @flow
 * Check for order amount
 * @if (amount > 0)
 *    Open modal to show loader and live messages: ctx.commit('pos/setPaymentMessage', ...)
 *    Pay with POS: posPayment()
 * @else close order normally
 * @arguments
 * ctx :  Store
 * code:  Payment_method
 * @return order_id
 * */
function giftPayment (ctx, code) {
  let activeGiftcards = ctx.getters['cart/getActiveGiftCards']
  if (activeGiftcards && activeGiftcards.length) {
    let amount = ctx.getters['cart/totalAmount']
    if (amount && amount > 0) {
      return posPayment(ctx)
    } else {
      return genericPayment(ctx)
        .then((orderId) => {
          let payload = { params: { 'c_type': paymentType['GIFT'] } }
          return updateOrder(ctx, orderId, payload).then(() => { return orderId })
        })
    }
  } else {
    // eslint-disable-next-line
    return Promise.reject({ err: { body: { result: 'giftCard.no-giftcard-provided' } }, orderId: null })
  }
}

/**
 * Process order using Gift payment
 * @flow
 * Save payment method (@argument code)
 * on success close order using checkout/placeOrder
 * @arguments
 * ctx :  Store
 * code:  Payment_method
 * @return order_id
 * */
function genericPayment (ctx, code) {
  ctx.commit('ui/showLoader')
  if (code) {
    return ctx.dispatch('checkout/setPaymentMethod', code)
      .then(() => {
        return ctx.dispatch('checkout/placeOrder')
          .then((order) => {
            return order.order_id
          })
          .finally(() => {
            ctx.commit('ui/hideLoader')
          })
      })
  } else {
    return ctx.dispatch('checkout/placeOrder')
      .then((order) => {
        return order.order_id
      })
      .finally(() => {
        ctx.commit('ui/hideLoader')
      })
  }
}

/**
 * Update order with payload
 * @arguments
 * ctx :  Store
 * orderId
 * payload
 * @return order_id
 * */
function updateOrder (ctx, orderId, payload = null) {
  if (!payload) {
    return Promise.resolve(orderId)
  } else {
    return ctx.dispatch('orders/updateOrder', { orderId, payload })
      .then(() => {
        return orderId
      })
      .catch(() => {
        return orderId
      })
  }
}

/**
 * Update order with payload
 * @arguments
 * ctx :  Store
 * orderId
 * payload
 * @return order_id
 * */
// function prepareParamsForUpdateOrder ({ order, paymentCode, payload = {} }) {
//   let params = { ...payload }
//   // switch (paymentCode) {
//   //   case 'POS':
//   //     // order in payments GIFT_CERTIFICATE
//   //     // payload['c_paidWithGC] = true
//   //     break
//   //   case 'adyen_pay_by_mail':
//   //     // order in payments GIFT_CERTIFICATE
//   //     // payload['c_paidWithGC] = true
//   //     break
//   //   case 'GIFT_CERTIFICATE':
//   //     // order in payments GIFT_CERTIFICATE
//   //     params['c_paidWithGC'] = true
//   //     break
//   //   default:
//   //     break
//   // }
//   return params
// }

function makeDispatch (ctx, { key, payloads = null }) {
  let composeDispatchFullKey = key
  if (ctx.state.pl_adyen) {
    composeDispatchFullKey = `pl_adyen/${key}`
  } else {
    composeDispatchFullKey = `pos/${key}`
  }
  return ctx.dispatch(composeDispatchFullKey, payloads)
}

/**
 * Load Modal component
 * @flow
 * Check for plugin or local component
 * @if pl_adyen exist get component from it
 * @elseif find theme components
 * @else use page loader as fallback
 * @arguments ctx
 * */
function loadModal (ctx) {
  let componentToLoad = ''
  if (ctx.state.pl_adyen) {
    componentToLoad = import('plugins/AdyenPayments/components/PaymentMethods/LivePay')
  } else {
    componentToLoad = import('theme/components/Checkout/PaymentMethods/POS/PosPay')
  }

  if (componentToLoad !== '') {
    setLoader(ctx, true)
    ctx.dispatch('ui/loadGenericModalComponent', {
      component: componentToLoad,
      modalProps: { hideFooter: true, hideHeader: true, noCloseOnBackdrop: true }
    })
  } else {
    // use loader
    ctx.commit('ui/showLoader')
  }
}

/**
 * Set loader
 * @flow
 * Check for plugin state or theme state
 * @if pl_adyen exist get it
 * @elseif find theme commit
 * @else nothing
 * @arguments ctx, loading
 * */
function setLoader (ctx, loading) {
  if (ctx.state.pl_adyen) {
    ctx.commit('pl_adyen/setPaymentLoading', loading)
  } else if (ctx.state.pos) {
    ctx.commit('pos/setPaymentLoading', loading)
  } else {
    return true
  }
}

/**
 * Set live message into component loaded by loadModal
 * @flow
 * Check for plugin state or theme state
 * @if pl_adyen exist get it
 * @elseif find theme commit
 * @else nothing
 * @arguments ctx, message
 * */
function setLiveMessage (ctx, message) {
  if (ctx.state.pl_adyen) {
    ctx.commit('pl_adyen/setPaymentMessage', message)
  } else if (ctx.state.pos) {
    ctx.commit('pos/setPaymentMessage', message)
  } else {
    return false
  }
}
